import React, { memo, useRef, useState } from 'react';
import { get } from 'lodash';
import styled from '@emotion/styled';
import { Image } from 'react-bootstrap';
import { FormControl, ControlLabel } from 'react-bootstrap';
import { useTransition, animated as a } from 'react-spring';

import colours from '~/utils/colours';
import { Step } from '~/App/Components/Typeform';
import ActionButton from '~/App/Components/Typeform/ActionButton';
import Scrollable from '~/App/Components/Typeform/Scrollable';
import QuestionTooltip from '~/App/Components/QuestionTooltip';
import { Profile } from '~/types/models';
import type { Contacts } from '~/types/redux/todo';

const PersonContainer = styled.div<{ hasError: boolean }>`
  width: 100%;
  display: flex;
  padding: 0.25em 0;
  user-select: none;
  align-items: center;
  color: ${colours.greyDark};
  justify-content: space-between;
  border-bottom: 1px solid ${colours.greyMedium};
  cursor: pointer;

  &:hover {
    background-color: ${colours.greyLightAlt};
  }

  label {
    font-size: 10px;
  }

  input {
    ${({ hasError }) => {
      return hasError
        ? `
      border-color:red;
      &:focus {
        border-color:red;
      }
      `
        : '';
    }}
  }
`;

const PersonName = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: 0.5em;
`;

const PersonDetails = styled.div`
  display: flex;
  align-items: center;
`;

type NameProps = {
  contactsArray: Contacts[];
  updateContact: (key: string, column: string, value: string) => void;
};

export interface PersonProps {
  name: string;
  email: string;
  image: string;
  unsubscribed: boolean;
  onChange?: (event: React.FormEvent<FormControl>) => void;
  onFocus?: (event: React.FocusEvent<FormControl>) => void;
}

const Unsubscribed = ({ show, id }: { show: boolean; id: string }) => {
  if (!show) {
    return null;
  }

  return (
    <ControlLabel>
      Unsubscribed &nbsp;
      <QuestionTooltip
        id={id}
        hoverText={<>This user has unsubscribed from receiving any emails.</>}
      />
    </ControlLabel>
  );
};

function Person({
  name,
  email,
  image,
  onChange,
  onFocus,
  unsubscribed,
}: PersonProps) {
  const [hasError, setHasError] = useState(false);

  const onInputChange = (event: React.FormEvent<FormControl>) => {
    setHasError((event.target as HTMLInputElement).value.trim() === '');

    if (typeof onChange === 'function') onChange(event);
  };
  return (
    <PersonContainer hasError={hasError}>
      <PersonDetails>
        <Image width={30} height={30} circle src={image} />
        <PersonName>
          <span>{email}</span>
          <Unsubscribed show={unsubscribed} id={'user-' + email} />
        </PersonName>
      </PersonDetails>
      <FormControl
        css={{ maxWidth: 'calc(50%)' }}
        defaultValue={name}
        onChange={onInputChange}
        onFocus={onFocus}
      />
    </PersonContainer>
  );
}

interface ContactItem {
  data: Profile;
  key: string;
  label: string;
}

const Names = memo(({ contactsArray, updateContact }: NameProps) => {
  const transitions = useTransition(contactsArray, (contact) => contact.key, {
    from: { opacity: 0, height: 53 },
    enter: { opacity: 1 },
    leave: { opacity: 0, height: 0 },
  });

  type Validation = Record<'string', boolean>;
  const [validations, setValidations] = useState<Validation>({} as Validation);
  const ref = useRef<HTMLDivElement | null | undefined>(null);
  const onNameChanged = (key, value) => {
    setValidations({ ...validations, [key]: value === '' });
    updateContact(key, 'name', value);
  };
  const hasError = Object.keys(validations).some((i) => validations[i]);
  return (
    <Step heading='Add Full Names'>
      <Scrollable elementRef={ref.current} itemCount={contactsArray.length}>
        <div ref={ref}>
          {transitions.map(({ key, item: contact, props }) => (
            <a.div key={key} style={props}>
              <Person
                name={get(contact, 'data.name')}
                email={get(contact, 'data.email', get(contact, 'data'))}
                image={get(contact, 'data.image')}
                unsubscribed={get(contact, 'data.unsubscribed')}
                onChange={(evt: React.FormEvent<FormControl>) =>
                  onNameChanged(
                    contact.key,
                    (evt.target as HTMLInputElement).value.trim()
                  )
                }
              />
            </a.div>
          ))}
        </div>
      </Scrollable>
      <ActionButton disabled={hasError} />
    </Step>
  );
});

export default Names;
